import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";

// Plain-English definitions for every term the briefing uses.
//
// A buyer reads the briefing without the analyst in the room. A figure they
// cannot interpret is decoration, not evidence - so this ships with the report
// rather than sitting on a separate help page nobody opens.
//
// Definitions come from backend/metric_definitions.py, the same source the PDF
// glossary reads, so the document a client keeps and the page they read it on
// cannot say different things.
//
// Collapsed by default: reference material, not a finding. Open on demand.
export default function MetricDefinitions({ scope = "sweep", coverage = "full" }) {
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get(`/metric-definitions`, { params: { scope, coverage } })
      .then((r) => live && setGroups(r.data?.groups || []))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [scope, coverage]);

  // A glossary that fails to load must never take the report down with it.
  if (failed || (groups && groups.length === 0)) return null;

  const count = (groups || []).reduce((n, g) => n + g.terms.length, 0);

  return (
    <section className="card-cream p-6 md:p-8" data-testid="metric-definitions">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-4 text-left group"
      >
        <span>
          <span className="tick mb-2 block text-[#9a3412]">Definitions</span>
          <span className="font-display text-[22px] md:text-[26px] text-[#171717] block leading-tight">
            Every term in this briefing, in plain English
          </span>
          <span className="font-mono-tech text-[12px] text-[#525252] block mt-2 max-w-2xl leading-relaxed">
            What each figure means, how it was measured, and why it matters — so you
            can check the working rather than take it on trust.
            {count > 0 && ` ${count} terms.`}
          </span>
        </span>
        <ChevronDown
          className={`shrink-0 mt-1 h-5 w-5 text-[#525252] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="mt-6 space-y-7">
          {!groups && (
            <p className="font-mono-tech text-[12px] text-[#525252]">Loading definitions…</p>
          )}
          {(groups || []).map((g) => (
            <div key={g.group}>
              <div className="tick mb-3">{g.title}</div>
              <dl className="space-y-3">
                {g.terms.map((t) => (
                  <div
                    key={t.key}
                    className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-4"
                  >
                    <dt className="font-mono-tech text-[12px] font-semibold text-[#171717]">
                      {t.term}
                    </dt>
                    <dd className="mt-1.5">
                      <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                        {t.plain}
                      </p>
                      <p className="font-mono-tech text-[11px] text-[#525252] leading-relaxed mt-2">
                        <span className="text-[#9a3412]">Measured:</span> {t.measured}
                      </p>
                      <p className="font-mono-tech text-[11px] text-[#525252] leading-relaxed mt-1">
                        <span className="text-[#9a3412]">Why it matters:</span> {t.matters}
                      </p>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
